import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { isDemoMode, redirectToSignup } from '../lib/demoMode';
import { IconClients, IconDashboard, IconExpenses, IconInvoices, IconMenu, IconMoon, IconProfile, IconProjects, IconSun, IconWorkProof } from '../lib/icons';
// 収支分析・AIアドバイスを再表示するときは、上の import に IconAnalytics, IconAi を戻す
import { useTheme } from '../lib/ThemeContext';
import { THEMES } from '../lib/theme';

const NAV_ITEMS = [
  { path: '/',           icon: IconDashboard,  label: 'ダッシュボード' },
  { path: '/clients',    icon: IconClients,    label: 'クライアント' },
  { path: '/projects',   icon: IconProjects,   label: '案件管理' },
  { path: '/expenses',   icon: IconExpenses,   label: '経費管理' },
  { path: '/invoices',   icon: IconInvoices,   label: '取引管理' },
  // { path: '/analytics',  icon: IconAnalytics,  label: '収支分析' },   // 一時的に非表示（後で戻すかも）
  // { path: '/ai',         icon: IconAi,         label: 'AIアドバイス' }, // 一時的に非表示（後で戻すかも）
  { path: '/work-proof', icon: IconWorkProof,  label: '稼働証明書' },
];

export default function Layout({ children }) {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { theme, themeKey, switchTheme } = useTheme();
  const [menuOpen,   setMenuOpen]   = useState(false);
  const [userName,   setUserName]   = useState('');
  const [userEmail,  setUserEmail]  = useState('');

  useEffect(() => {
    const fetchUser = async () => {
      if (isDemoMode()) { setUserName('デモユーザー'); setUserEmail(''); return; }
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      setUserEmail(user.email || '');
      const { data } = await supabase.from('profiles').select('name').eq('id', user.id).single();
      if (data?.name) setUserName(data.name);
      else setUserName(user.email?.split('@')[0] || 'ユーザー');
    };
    fetchUser();
  }, [location]);

  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const handleNav = (path) => { navigate(path); setMenuOpen(false); };

  // テーマ切り替えボタン
  const ThemeSwitcher = () => (
    <div style={{ display: 'flex', gap: 4, padding: '8px 10px' }}>
      {Object.entries(THEMES).map(([key, t]) => (
        <button key={key} onClick={() => switchTheme(key)} title={t.name}
          style={{
            flex: 1, padding: '6px 0', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13,
            background: themeKey === key ? theme.accent : theme.bgInput,
            color:      themeKey === key ? '#fff' : theme.textMuted,
            fontWeight: themeKey === key ? 700 : 400,
            transition: 'all 0.15s',
          }}>
          <span style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
            {key === 'dark' ? <IconMoon size={14} color={themeKey === key ? '#fff' : theme.textMuted}/> : <IconSun size={14} color={themeKey === key ? '#fff' : theme.textMuted}/>}
            {t.name}
          </span>
        </button>
      ))}
    </div>
  );

  const sidebarContent = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: theme.bgDeep }}>
      {/* ロゴ */}
      <div
        onClick={() => handleNav('/')}
        style={{ padding: '20px 20px 18px', borderBottom: `1px solid ${theme.border}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
      >
        <svg width="34" height="34" viewBox="0 0 192 192" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink:0, borderRadius:10 }}>
          <rect width="192" height="192" rx="42" fill="url(#grad)"/>
          <rect x="38" y="38" width="116" height="34" rx="10" fill="white"/>
          <rect x="38" y="84" width="64" height="34" rx="10" fill="#9B59E8"/>
          <rect x="112" y="84" width="42" height="34" rx="10" fill="#7BA7C8"/>
          <rect x="38" y="130" width="90" height="34" rx="10" fill="#3DBB7C"/>
          <defs>
            <linearGradient id="grad" x1="0" y1="0" x2="192" y2="192" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#5B6FD4"/>
              <stop offset="100%" stopColor="#3DBB7C"/>
            </linearGradient>
          </defs>
        </svg>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: theme.accent, letterSpacing: '-0.5px', lineHeight: 1 }}>Totonoee</div>
          <div style={{ fontSize: 9, color: theme.textWeak, marginTop: 2, letterSpacing: '0.1em' }}>FREELANCE NOTE</div>
        </div>
      </div>

      {/* ナビ */}
      <nav style={{ flex: 1, padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {NAV_ITEMS.map(item => (
          <button key={item.path} onClick={() => handleNav(item.path)}
            style={{
              width: '100%', textAlign: 'left', padding: '10px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, transition: 'all 0.15s',
              fontWeight: isActive(item.path) ? 600 : 400,
              background: isActive(item.path) ? theme.accentBg : 'transparent',
              color:      isActive(item.path) ? theme.accent   : theme.textMuted,
            }}>
            <item.icon size={18} color={isActive(item.path) ? theme.accent : theme.textMuted} />
            {item.label}
          </button>
        ))}
      </nav>

      {/* テーマ切り替え */}
      <div style={{ borderTop: `1px solid ${theme.border}` }}>
        <ThemeSwitcher />
      </div>

      {/* プロフィール */}
      <div style={{ padding: '8px 10px 12px', borderTop: `1px solid ${theme.border}` }}>
        {isDemoMode() ? (
          <button onClick={redirectToSignup}
            style={{
              width: '100%', textAlign: 'left', padding: '12px 14px', borderRadius: 10,
              border: `1px solid ${theme.demoBorder}`, cursor: 'pointer',
              display: 'flex', flexDirection: 'column', gap: 3, transition: 'all 0.15s',
              background: theme.demoBg,
            }}>
            <div style={{ fontSize: 11, color: theme.demoText1, fontWeight: 600 }}>🎮 こちらはデモ版です</div>
            <div style={{ fontSize: 13, color: theme.demoText2, fontWeight: 700 }}>新規登録をする →</div>
          </button>
        ) : (
          <button onClick={() => handleNav('/profile')}
            style={{
              width: '100%', textAlign: 'left', padding: '10px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 10, transition: 'all 0.15s',
              background: isActive('/profile') ? theme.accentBg : 'transparent',
            }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
              {userName.charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: theme.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</div>
              <div style={{ fontSize: 11, color: theme.textWeak, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userEmail}</div>
            </div>
            <IconProfile size={16} color={theme.textWeak} />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', height: '100vh', background: theme.bg, color: theme.textPrimary, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

      {/* デスクトップサイドバー */}
      <div style={{ width: 220, flexShrink: 0, borderRight: `1px solid ${theme.border}`, display: 'flex', flexDirection: 'column', boxShadow: theme.shadow }} className="desktop-sidebar">
        {sidebarContent}
      </div>

      {/* モバイルオーバーレイ */}
      {menuOpen && (
        <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 40 }} />
      )}

      {/* モバイルドロワー */}
      <div style={{ position: 'fixed', top: 0, left: 0, height: '100%', width: 240, borderRight: `1px solid ${theme.border}`, zIndex: 50, transform: menuOpen ? 'translateX(0)' : 'translateX(-100%)', transition: 'transform 0.25s ease', display: 'flex', flexDirection: 'column', paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)', paddingLeft: 'env(safe-area-inset-left)' }}>
        {sidebarContent}
      </div>

      {/* メインコンテンツ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* モバイルトップバー */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', paddingTop: 'calc(12px + env(safe-area-inset-top))', borderBottom: `1px solid ${theme.border}`, background: theme.bgDeep }} className="mobile-topbar">
          <button onClick={() => setMenuOpen(!menuOpen)} style={{ background: 'none', border: 'none', color: theme.textSecondary, cursor: 'pointer', padding: 4, display:'flex', alignItems:'center' }}><IconMenu size={22} color={theme.textSecondary} /></button>
          <div
            onClick={() => handleNav('/')}
            style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}
          >
            <svg width="26" height="26" viewBox="0 0 192 192" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ borderRadius:7 }}>
              <rect width="192" height="192" rx="42" fill="url(#gradm)"/>
              <rect x="38" y="38" width="116" height="34" rx="10" fill="white"/>
              <rect x="38" y="84" width="64" height="34" rx="10" fill="#9B59E8"/>
              <rect x="112" y="84" width="42" height="34" rx="10" fill="#7BA7C8"/>
              <rect x="38" y="130" width="90" height="34" rx="10" fill="#3DBB7C"/>
              <defs>
                <linearGradient id="gradm" x1="0" y1="0" x2="192" y2="192" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#5B6FD4"/>
                  <stop offset="100%" stopColor="#3DBB7C"/>
                </linearGradient>
              </defs>
            </svg>
            <span style={{ fontSize: 16, fontWeight: 800, color: theme.accent, letterSpacing: '-0.3px' }}>Totonoee</span>
          </div>
          <button
            onClick={() => isDemoMode() ? redirectToSignup() : handleNav('/profile')}
            style={{ width: 32, height: 32, borderRadius: '50%', background: isDemoMode() ? '#f97316' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#fff' }}>
            {isDemoMode() ? '🎮' : userName.charAt(0).toUpperCase()}
          </button>
        </div>

        <main style={{ flex: 1, overflowY: 'auto', paddingBottom: 'env(safe-area-inset-bottom)' }}>
          {children}
        </main>
      </div>

      <style>{`
        @media (min-width: 768px) { .desktop-sidebar { display: flex !important; } .mobile-topbar { display: none !important; } }
        @media (max-width: 767px) { .desktop-sidebar { display: none !important; } .mobile-topbar { display: flex !important; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; }
        body { margin: 0; background: ${theme.bg}; overflow-x: hidden; }
        html { overflow-x: hidden; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${theme.border}; border-radius: 2px; }
      `}</style>
    </div>
  );
}
